import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // Add this import
import { supportedLanguages } from '../supportedLanguages';

import { Helmet } from 'react-helmet-async';

const WelcomePage = () => {
  const { lang } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(); // Add this line
  const language = lang && supportedLanguages.includes(lang)
    ? lang
    : (supportedLanguages.includes(navigator.language.slice(0, 2))
        ? navigator.language.slice(0, 2)
        : 'fr');
  const langPrefix = lang && supportedLanguages.includes(lang) ? `/${lang}` : `/${language}`;

  // Canonical URL
  const canonicalUrl = `https://bible4.me${langPrefix}/welcome`;

  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        // Load the Markdown file based on the language
        const response = await fetch(`/locales/${language}/welcome.md`);
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const text = await response.text();
        setContent(text);
      } catch (error) {
        console.error('Error loading content:', error);
        // Fallback to French if the requested language is not available
        if (language !== 'fr') {
          const fallbackResponse = await fetch('/locales/fr/welcome.md');
          if (fallbackResponse.ok) {
            const fallbackText = await fallbackResponse.text();
            setContent(fallbackText);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [language]);

  const handleCreateNote = () => {
    window.location.href = `https://bible4.me/app`;
  };

  const handleDailyReadings = () => {
    window.location.href = `${langPrefix}/daily-readings`;
  };

  const handleReturnHome = () => {
    // Always go to the current :lang root, not the detected browser language
    navigate(`/${lang && supportedLanguages.includes(lang) ? lang : language}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-900"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('appTitle')}</title>
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      <div className="bg-white rounded-lg shadow-md max-w-3xl mx-auto p-6">
      {/* Add a return/back button at the top */}
      <div className="mb-4">
        <button
          onClick={handleReturnHome}
          className="text-amber-900 hover:underline font-medium"
        >
          {t('common.backToHome', "← Retour à l'accueil")}
        </button>
      </div>
      <ReactMarkdown
        children={content}
        components={{
          h1: ({ node, ...props }) => (
            <h1 className="text-2xl font-bold text-amber-900 mb-4" {...props} />
          ),
          h2: ({ node, ...props }) => (
            <h2 className="text-xl font-bold text-amber-900 mt-6 mb-3" {...props} />
          ),
          p: ({ node, ...props }) => (
            <p className="text-amber-800 mb-4" {...props} />
          ),
          ul: ({ node, ...props }) => (
            <ul className="list-disc pl-5 mb-4 text-amber-800" {...props} />
          ),
          li: ({ node, ...props }) => (
            <li className="mb-1" {...props} />
          ),
          a: ({ node, ...props }) => {
            let href = props.href || '';
            // Prepend langPrefix to relative links (not starting with http, mailto, #, or already with langPrefix)
            if (
              href &&
              !href.startsWith('http') &&
              !href.startsWith('mailto:') &&
              !href.startsWith('#') &&
              !href.startsWith(langPrefix)
            ) {
              href = `${langPrefix}${href.startsWith('/') ? '' : '/'}${href}`;
            }
            return (
              <a
                className="text-amber-900 hover:text-amber-700 underline"
                target={props.href?.startsWith('http') ? '_blank' : undefined}
                rel={props.href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                href={href}
                {...props}
              />
            );
          },
          hr: ({ node, ...props }) => (
            <hr className="my-6 border-amber-200" {...props} />
          ),
          em: ({ node, ...props }) => (
            <em className="text-amber-900 font-medium not-italic" {...props} />
          ),
        }}
      />

      <div className="flex justify-center gap-4 mt-8">
        <button
          onClick={handleCreateNote}
          className="bg-amber-900 hover:bg-amber-800 text-white px-5 py-2 rounded-lg transition-colors"
        >
          {t('welcome.createFirstNote', 'Créer ma première note')}
        </button>
        <button
          onClick={handleDailyReadings}
          className="border-2 border-amber-900 text-amber-900 px-5 py-2 rounded-lg hover:bg-amber-100 transition-colors"
        >
          {t('welcome.dailyReadings', 'Lectures du jour')}
        </button>
      </div>
      </div>
    </>
  );
};

export default WelcomePage;